import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaPlay, FaStepForward } from 'react-icons/fa';
import './Hero.css';

const HOVER_PREVIEW_DELAY_MS = 500;
const HERO_SWITCH_DELAY_MS = 20000;
const heroPointerState =
  typeof window !== 'undefined'
    ? (window.__eddieHeroPointerState || (window.__eddieHeroPointerState = { x: null, y: null }))
    : { x: null, y: null };

if (typeof window !== 'undefined' && !window.__eddieHeroPointerTracking) {
  const updatePointerPosition = (event) => {
    heroPointerState.x = event.clientX;
    heroPointerState.y = event.clientY;
  };

  window.addEventListener('pointermove', updatePointerPosition, { passive: true });
  window.addEventListener('pointerdown', updatePointerPosition, { passive: true });
  window.__eddieHeroPointerTracking = true;
}

const Hero = ({ hero, secondaryHero, onPlayVideo, isSecondary = false, isActive = true }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeHero, setActiveHero] = useState(hero);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPreviewMuted, setIsPreviewMuted] = useState(false);
  
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const switchTimeoutRef = useRef(null);
  const previousHeroUrlRef = useRef(hero.videoUrl);

  useEffect(() => {
    setActiveHero(hero);
  }, [hero]);

  const resetVideo = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  }, []);

  useEffect(() => {
    if (isActive) {
      return;
    }

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
    }

    setIsHovered(false);
    setIsTransitioning(false);
    setIsPreviewMuted(false);
    setActiveHero(hero);
    resetVideo();
  }, [hero, isActive, resetVideo]);

  const playPreview = useCallback(async () => {
    if (!videoRef.current) {
      return false;
    }

    const video = videoRef.current;

    video.defaultMuted = false;
    video.muted = false;
    video.volume = 1;
    setIsPreviewMuted(false);

    try {
      await video.play();
      return true;
    } catch (error) {
      video.defaultMuted = true;
      video.muted = true;
      setIsPreviewMuted(true);

      try {
        await video.play();
        return true;
      } catch (fallbackError) {
        return false;
      }
    }
  }, []);

  const startPreview = useCallback((delay = HOVER_PREVIEW_DELAY_MS) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(async () => {
      setIsHovered(true);
      await playPreview();
    }, delay);
  }, [playPreview]);

  const isPointerInsideSection = useCallback(() => {
    if (!sectionRef.current) {
      return false;
    }

    const { x, y } = heroPointerState;

    if (x === null || y === null) {
      return false;
    }

    const rect = sectionRef.current.getBoundingClientRect();
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }, []);

  const switchHero = (nextHero) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveHero(nextHero);
      setIsTransitioning(false);
    }, 500);
  };

  const handleSwitch = (e) => {
    if (e) e.stopPropagation();
    if (secondaryHero) {
      const nextHero = activeHero === hero ? secondaryHero : hero;
      switchHero(nextHero);
    }
  };

  const handleMouseEnter = () => {
    startPreview();
  };

  // Effect to handle carousel cycling
  useEffect(() => {
    if (isActive && isHovered && secondaryHero && !isSecondary) {
      const nextHero = activeHero === hero ? secondaryHero : hero;
      
      // Clear any existing switch timeout
      if (switchTimeoutRef.current) clearTimeout(switchTimeoutRef.current);

      switchTimeoutRef.current = setTimeout(() => {
        switchHero(nextHero);
      }, HERO_SWITCH_DELAY_MS);
    }

    return () => {
      if (switchTimeoutRef.current) clearTimeout(switchTimeoutRef.current);
    };
  }, [isActive, isHovered, activeHero, secondaryHero, hero, isSecondary]);

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (switchTimeoutRef.current) clearTimeout(switchTimeoutRef.current);
    
    setIsHovered(false);
    
    if (!isSecondary) {
      setActiveHero(hero);
    }

    resetVideo();
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (switchTimeoutRef.current) clearTimeout(switchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const heroChanged = previousHeroUrlRef.current !== activeHero.videoUrl;
    previousHeroUrlRef.current = activeHero.videoUrl;

    if (isActive && heroChanged && videoRef.current && isHovered) {
      playPreview();
    }
  }, [activeHero, isActive, isHovered, playPreview]);

  useEffect(() => {
    if (isMobile || !isActive || isHovered || !sectionRef.current) {
      return;
    }

    if (isPointerInsideSection()) {
      startPreview();
    }
  }, [activeHero, isActive, isHovered, isMobile, isPointerInsideSection, startPreview]);

  useEffect(() => {
    if (!isMobile || !isActive) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsHovered(true);
            playPreview();
          } else {
            setIsHovered(false);
            resetVideo();
          }
        });
      },
      { threshold: 0.6 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, [isActive, isMobile, playPreview, resetVideo]);

  return (
    <section
      ref={sectionRef}
      className="hero-section"
      style={{ backgroundImage: `url(${activeHero.backgroundImage})` }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {activeHero.videoUrl && (
        <video
          key={activeHero.videoUrl}
          ref={videoRef}
          className={`hero-video-preview ${isHovered ? 'visible' : ''} ${isTransitioning ? 'fading' : ''}`}
          src={activeHero.videoUrl}
          muted={isPreviewMuted}
          loop
          playsInline
          preload="auto"
        />
      )}
      <div className="hero-overlay"></div>
      <div className={`hero-content ${isTransitioning ? 'fading' : ''}`}>
        <div className="hero-text">
          {activeHero.sponsor && (
            <div className="hero-sponsor">
              <span className="sponsor-label">Presented by</span>
              <span className="sponsor-name">{activeHero.sponsor}</span>
            </div>
          )}
          <h2 className="hero-title">{activeHero.title}</h2>
          <p className="hero-subtitle">{activeHero.subtitle}</p>
          <p className="hero-description">{activeHero.description}</p>
          
          <div className="hero-buttons">
            <button
              className="hero-cta"
              onClick={() => onPlayVideo(activeHero.title, activeHero.videoUrl)}
            >
              <FaPlay className="play-icon" />
              {activeHero.cta}
            </button>
          </div>
        </div>
      </div>

      {secondaryHero && (
        <>
          <button 
            className="hero-switch-btn side-control"
            onClick={handleSwitch}
            aria-label="Switch to next show"
          >
            <FaStepForward />
          </button>
          
          <div className="hero-dots">
            <button 
              className={`hero-dot ${activeHero === hero ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (activeHero !== hero) switchHero(hero);
              }}
              aria-label="Show first video"
            />
            <button 
              className={`hero-dot ${activeHero === secondaryHero ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (activeHero !== secondaryHero) switchHero(secondaryHero);
              }}
              aria-label="Show second video"
            />
          </div>
        </>
      )}
    </section>
  );
};

export default Hero;
